<script setup lang="ts">
/**
 * `/looks/:id` — the address outfits had before they carried a slug.
 *
 * It shipped in `feat: shop the look (#1144)`, so it is out there: in anything
 * anyone shared, and in whatever a crawler has indexed. Nothing on the site
 * links to it any more and no canonical names it, but deleting the route would
 * turn every one of those into a 404, and that is not recoverable.
 *
 * So it redirects, permanently, to the slugged address — one lookup by id, the
 * same read the outfit page itself does, so a cache hit serves it. An outfit
 * that no longer resolves gets the 404 it would have got anyway.
 *
 * This is also why `pages:extend` in `nuxt.config.ts` matches the outfit page
 * by FILE rather than by path: both routes begin `/looks/:id`, and cloning this
 * one to `/preview/o/:id` would have made the merchandiser preview a redirect.
 */
definePageMeta({ layout: false })

const route = useRoute()
const localePath = useLocalePath()
const id = computed(() => String(route.params.id ?? ''))

const { data: look } = await useLook(id, false)

if (!look.value)
  throw createError({ statusCode: 404, statusMessage: 'Outfit not found' })

await navigateTo(
  { path: localePath(`/looks/${id.value}/${look.value.slug}`), query: route.query },
  { redirectCode: 301, replace: true },
)
</script>

<template>
  <div />
</template>
